import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import RenameIcon from "@mui/icons-material/DriveFileRenameOutlineRounded";
import { Stack, Typography } from "@mui/material";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon";
import type { GridBasicGroupNode, GridRowId } from "@mui/x-data-grid-premium";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import ExpandCollapseButton from "@entrilia/shared/components/ExpandCollapseButton";
import ClientLogo from "../../home/ClientLogo";
import { useConfigurationsGridActionsContext } from "./ConfigurationsGridActionsContext";

export interface ConfigurationRow {
  id: string;
  group: string;
  configurationId: string;
  clientCode: string;
  clientTitle: string;
  clientLogoUrl: string | undefined;
  isEmpty?: boolean;
}

interface ConfigurationNameGroupingCellProps {
  id: GridRowId;
  rowNode: GridBasicGroupNode;
  name: string;
}

export const ConfigurationNameGroupingCell = ({ id, rowNode, name }: ConfigurationNameGroupingCellProps) => {
  const apiRef = useGridApiContext();

  const handleToggle = () => {
    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
  };

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", maxWidth: "100%" }}>
      <ExpandCollapseButton
        expanded={rowNode.childrenExpanded === true}
        onToggle={handleToggle}
        tabIndex={-1}
        ExpandIcon={ExpandIcon}
        CollapseIcon={CollapseIcon}
      />
      <Typography variant="subtitle2" noWrap>
        {name}
      </Typography>
    </Stack>
  );
};

export const ClientTitleCell = ({ row }: { row: ConfigurationRow }) => (
  <Stack direction="row" spacing={1.5} sx={{ pl: 5, alignItems: "center" }}>
    {row.isEmpty && <Typography color="textSecondary">This configuration is not used by any company</Typography>}
    {!row.isEmpty && (
      <>
        <ClientLogo logoSrc={row.clientLogoUrl} size="small" />
        <Typography noWrap>{row.clientTitle}</Typography>
      </>
    )}
  </Stack>
);

export const ConfigurationActionsCell = ({ configurationId }: { configurationId: string }) => {
  const { onRenameConfiguration, onDeleteConfiguration } = useConfigurationsGridActionsContext();

  return (
    <ActionsMenuButton
      items={[
        {
          label: "Rename",
          icon: <RenameIcon color="secondary" />,
          onClick: () => onRenameConfiguration(configurationId),
        },
        {
          label: "Delete",
          icon: <DeleteIcon color="error" />,
          onClick: () => onDeleteConfiguration(configurationId),
        },
      ]}
    />
  );
};
